// Mobile-only in-place list search/filter field.
//
// The Mixpedia + Set Planner searches on phones use the full-screen
// SoundCloud-style overlay (MobileSearchOverlay / MobileTypedSearchOverlay).
// List *filters* (Likes, Playlists, Favourite Artists, Digger source picker,
// Add to Playlist…) used to be bare 12px <input type="text"> boxes: no glass
// icon, no clear button, no way to dismiss the keyboard, and — the real
// problem — with the keyboard up the list they filter is squashed into two
// rows between the field, the mini-player and the bottom nav.
//
// So on phones the field is a TRIGGER: tapping it opens the same full-screen
// overlay, seeded with the whole list; typing narrows it live (local, no
// debounce); tapping a row runs that row's action (open the artist / track,
// expand the playlist, pick the source…); the "Search" key hands the query
// back to the underlying list and closes. The pill then shows the applied
// query with a × to clear it.
//
// Callers keep their desktop <input> untouched and render this ONLY on the
// mobile path (`useIsMobileWeb()`), so desktop/Electron markup is
// bit-identical to before.
//
//   <MobileListSearch
//       value={query} onChange={setQuery} placeholder="Search likes"
//       overlay={{
//           fetch: (q) => rows,            // rows: { key, title, subtitle?, artworkUrl?, artworkWrapClassName?, round?, item }
//           onSelect: (row) => …,          // row.item is the caller's own object
//       }}
//   />
//
// Without `overlay` it degrades to a plain in-place pill (× clear, Enter
// blurs, iOS-style Cancel) — used where a full-screen takeover makes no
// sense (e.g. inside another full-screen sheet).
import React, { useRef, useState } from 'react';
import MobileTypedSearchOverlay from '../MobileTypedSearchOverlay';
import './MobileListSearch.scss';

// Every word must match somewhere, any order — "prospa baby" has to find
// "Prospa, Murda Beatz - Baby", which a whole-query substring test never
// does. Empty query matches everything.
export function matchesQuery(text, query) {
    const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return true;
    const hay = String(text || '').toLowerCase();
    return words.every((w) => hay.includes(w));
}

const MobileListSearch = ({
    value,
    onChange,
    placeholder = 'Search',
    ariaLabel,
    className = '',
    autoFocus = false,
    overlay = null,
    // Optional: called after Cancel (cleared + blurred). Lets a modal close
    // itself on Cancel if it wants; most callers ignore it.
    onCancel,
    // Optional: forward keydown so callers can keep their Escape-closes-modal
    // handling; MobileListSearch handles Enter/Escape first.
    onKeyDown,
    inputRef: inputRefProp,
}) => {
    const localRef = useRef(null);
    const inputRef = inputRefProp || localRef;
    const [focused, setFocused] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(false);
    const hasValue = !!(value && String(value).length);
    const asTrigger = !!overlay;
    const showCancel = !asTrigger && (focused || hasValue);

    const clear = () => {
        onChange('');
        if (!asTrigger) inputRef.current?.focus();
    };

    const cancel = () => {
        onChange('');
        setFocused(false);
        inputRef.current?.blur();
        if (onCancel) onCancel();
    };

    const openOverlay = () => {
        // Don't let iOS open the keyboard for the inline field — the
        // overlay's own input handles typing.
        inputRef.current?.blur();
        setOverlayOpen(true);
    };

    const handleKeyDown = (e) => {
        if (asTrigger) {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOverlay(); }
            return;
        }
        if (e.key === 'Enter') {
            // Dismiss the keyboard, keep the filter applied.
            e.preventDefault();
            inputRef.current?.blur();
            return;
        }
        if (e.key === 'Escape') {
            if (hasValue) {
                e.preventDefault();
                e.stopPropagation();
                clear();
                return;
            }
        }
        if (onKeyDown) onKeyDown(e);
    };

    return (
        <div className={`mobile-list-search${showCancel ? ' is-active' : ''}${asTrigger ? ' is-trigger' : ''}${className ? ` ${className}` : ''}`}>
            <div className="mls-pill" onClick={asTrigger ? openOverlay : undefined}>
                <svg className="mls-glass" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input
                    ref={inputRef}
                    type="search"
                    inputMode="search"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    className="mls-input"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => { if (asTrigger) openOverlay(); else setFocused(true); }}
                    onBlur={() => setFocused(false)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    aria-label={ariaLabel || placeholder}
                    autoFocus={autoFocus}
                    readOnly={asTrigger}
                />
                {hasValue && (
                    <button
                        type="button"
                        className="mls-clear"
                        // preventDefault on mousedown keeps focus (and the
                        // keyboard) on the input while clearing.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => { e.stopPropagation(); clear(); }}
                        aria-label="Clear search"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
            </div>
            {!asTrigger && (
                <button
                    type="button"
                    className="mls-cancel"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={cancel}
                    tabIndex={showCancel ? 0 : -1}
                    aria-hidden={!showCancel}
                >
                    Cancel
                </button>
            )}
            {asTrigger && (
                <MobileTypedSearchOverlay
                    open={overlayOpen}
                    onClose={() => setOverlayOpen(false)}
                    placeholder={placeholder}
                    minChars={0}
                    debounceMs={0}
                    initialQuery={value || ''}
                    fetchSuggestions={overlay.fetch}
                    onSuggestionSelect={(row) => overlay.onSelect && overlay.onSelect(row)}
                    onSubmit={(q) => onChange(q)}
                />
            )}
        </div>
    );
};

export default MobileListSearch;
